import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import ClientOnly from "@/components/ClientOnly";
import {
  ArrowRight,
  ShieldCheck,
  Check,
  X,
  CreditCard,
  Smartphone,
  Globe,
  Monitor,
  Terminal,
  Wallet,
  Server,
  Repeat,
  Lock,
  ReceiptText,
  Building2,
  Quote,
  ScanLine,
  Cpu,
  PiggyBank,
  Settings,
  ArrowUpRight,
  Zap,
  Sparkles,
  Utensils,
  ShoppingBag,
  Store,
  Briefcase,
} from "lucide-react";
import Counter from "@/components/Counter";
import CTASection from "@/components/CTASection";
import {
  GrowthIllustration,
  EcosystemIllustration,
  AnywhereIllustration,
} from "@/components/illustrations/FintechIllustrations";
import ParticleField from "@/components/ParticleField";

const HERO_IMG =
  "https://images.unsplash.com/photo-1556742031-c6961e8560b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const DASHBOARD_IMG =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const MOBILE_IMG =
  "https://images.pexels.com/photos/11316617/pexels-photo-11316617.jpeg?auto=compress&cs=tinysrgb&w=1200";
const BENEFITS_IMG =
  "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const PRODUCTS_IMG =
  "https://images.unsplash.com/photo-1750263160670-42be92c0eaf0?fm=jpg&q=85&w=1200&auto=format&fit=crop";

/* ────────────────────────────────────────────────────────────
   PAYMENT TERMINAL ILLUSTRATION — premium fintech mockup for
   the "Payment solutions built around your business" section
   ──────────────────────────────────────────────────────────── */
function PaymentTerminalIllustration() {
  return (
    <div className="relative w-full max-w-[600px] mx-auto" data-testid="payment-terminal-illustration">
      {/* soft halo */}
      <div
        aria-hidden="true"
        className="absolute inset-x-6 top-10 bottom-10 rounded-[60px] pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,114,255,0.30) 0%, transparent 70%)",
          filter: "blur(50px)",
        }}
      />
      {/* floating accent dots */}
      <div aria-hidden="true" className="absolute top-[8%] right-[8%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
      <div aria-hidden="true" className="absolute top-[24%] left-[6%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-70" />
      <div aria-hidden="true" className="absolute bottom-[18%] right-[14%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60" />
      <div aria-hidden="true" className="absolute bottom-[12%] left-[12%] w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] opacity-55" />

      <svg viewBox="0 0 600 600" className="relative w-full h-auto" aria-hidden="true">
        <defs>
          <linearGradient id="pt-terminal-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10182A" />
            <stop offset="50%" stopColor="#0A1224" />
            <stop offset="100%" stopColor="#061A3A" />
          </linearGradient>
          <linearGradient id="pt-screen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0072FF" />
            <stop offset="100%" stopColor="#3A8EFF" />
          </linearGradient>
          <linearGradient id="pt-key" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1F2A44" />
            <stop offset="100%" stopColor="#0F1729" />
          </linearGradient>
          <linearGradient id="pt-glare" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.25" />
            <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="pt-card" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0A2552" />
            <stop offset="100%" stopColor="#0072FF" />
          </linearGradient>
          <filter id="pt-deep-shadow" x="-30%" y="-20%" width="160%" height="140%">
            <feDropShadow dx="0" dy="36" stdDeviation="28" floodColor="#061A3A" floodOpacity="0.28" />
          </filter>
          <filter id="pt-card-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="14" stdDeviation="18" floodColor="#061A3A" floodOpacity="0.20" />
          </filter>
          <pattern id="pt-dots" width="18" height="18" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#0072FF" fillOpacity="0.16" />
          </pattern>
        </defs>

        {/* dotted backdrop band */}
        <rect x="40" y="80" width="520" height="440" rx="40" fill="url(#pt-dots)" opacity="0.55" />

        {/* contactless waves emanating from above the screen */}
        <g transform="translate(300 100)" opacity="0.55">
          <path d="M-22 0 Q0 -22 22 0" fill="none" stroke="#0072FF" strokeWidth="3" strokeLinecap="round">
            <animate attributeName="opacity" values="0.65;0.2;0.65" dur="2.4s" repeatCount="indefinite" />
          </path>
          <path d="M-34 6 Q0 -34 34 6" fill="none" stroke="#0072FF" strokeWidth="3" strokeLinecap="round" opacity="0.7">
            <animate attributeName="opacity" values="0.45;0.1;0.45" dur="2.4s" begin="0.4s" repeatCount="indefinite" />
          </path>
          <path d="M-46 14 Q0 -46 46 14" fill="none" stroke="#0072FF" strokeWidth="3" strokeLinecap="round" opacity="0.4">
            <animate attributeName="opacity" values="0.30;0;0.30" dur="2.4s" begin="0.8s" repeatCount="indefinite" />
          </path>
        </g>

        {/* FLOATING CARD — back-left, slightly rotated */}
        <g transform="translate(40 200) rotate(-12)" filter="url(#pt-card-shadow)">
          <rect width="170" height="106" rx="14" fill="url(#pt-card)" />
          <rect width="170" height="106" rx="14" fill="url(#pt-glare)" />
          {/* chip */}
          <rect x="14" y="36" width="28" height="22" rx="4" fill="#C5A878" />
          <rect x="17" y="39" width="22" height="16" rx="3" fill="#D8B888" />
          <rect x="19" y="42" width="18" height="1.4" rx="0.7" fill="#9C7E5A" />
          <rect x="19" y="46" width="18" height="1.4" rx="0.7" fill="#9C7E5A" />
          <rect x="19" y="50" width="18" height="1.4" rx="0.7" fill="#9C7E5A" />
          {/* contactless mini */}
          <g transform="translate(50 40)" stroke="#FFFFFF" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.9">
            <path d="M0 6 Q3 0 6 6" />
            <path d="M-2 10 Q3 -1 8 10" opacity="0.7" />
          </g>
          {/* number */}
          <text x="14" y="78" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="700" fill="#FFFFFF" opacity="0.92" letterSpacing="1">
            ●●●●  ●●●●  ●●●●  1245
          </text>
          <text x="14" y="94" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="700" fill="#FFFFFF" opacity="0.6">
            VALID 12/29
          </text>
          {/* brand dots */}
          <g transform="translate(130 84)">
            <circle r="8" fill="#EB001B" />
            <circle cx="12" r="8" fill="#F79E1B" />
          </g>
        </g>

        {/* MAIN TERMINAL — countertop POS */}
        <g transform="translate(170 130)" filter="url(#pt-deep-shadow)">
          {/* receipt printer top */}
          <rect x="40" y="-10" width="240" height="22" rx="6" fill="#1A2238" />
          <rect x="120" y="-4" width="80" height="3" rx="1.5" fill="#FFFFFF" opacity="0.3" />
          {/* receipt paper peeking */}
          <g transform="translate(76 -34)">
            <rect width="170" height="36" rx="3" fill="#FFFFFF" />
            <rect width="170" height="36" rx="3" fill="url(#pt-glare)" />
            <rect x="12" y="6" width="60" height="3" rx="1.5" fill="#061A3A" opacity="0.6" />
            <rect x="12" y="12" width="80" height="2.5" rx="1.2" fill="#061A3A" opacity="0.4" />
            <rect x="12" y="18" width="46" height="2.5" rx="1.2" fill="#061A3A" opacity="0.4" />
            <text x="140" y="24" textAnchor="end" fontFamily="ui-monospace, Menlo, monospace" fontSize="11" fontWeight="800" fill="#0072FF">
              $1,245
            </text>
          </g>

          {/* terminal body */}
          <rect x="20" y="10" width="280" height="360" rx="26" fill="url(#pt-terminal-body)" />
          {/* inner bezel */}
          <rect x="34" y="24" width="252" height="332" rx="18" fill="#06080F" />
          {/* metallic side highlight */}
          <rect x="20" y="10" width="280" height="360" rx="26" fill="url(#pt-glare)" opacity="0.4" />

          {/* SCREEN */}
          <rect x="48" y="40" width="224" height="170" rx="14" fill="url(#pt-screen)" />
          {/* inner radial highlight */}
          <ellipse cx="160" cy="76" rx="120" ry="46" fill="#FFFFFF" opacity="0.18" />
          {/* top status row */}
          <g transform="translate(60 56)">
            <circle cx="6" cy="6" r="4" fill="#FFFFFF" opacity="0.95">
              <animate attributeName="opacity" values="1;0.45;1" dur="1.8s" repeatCount="indefinite" />
            </circle>
            <text x="16" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF" opacity="0.85">SECURE</text>
            <text x="200" y="10" textAnchor="end" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#FFFFFF" opacity="0.7">
              9:41
            </text>
          </g>
          {/* PAYMENTS title — center */}
          <text x="160" y="118" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="30" fontWeight="900" fill="#FFFFFF" letterSpacing="2.2">
            PAYMENTS
          </text>
          {/* tiny underline */}
          <rect x="138" y="128" width="44" height="3" rx="1.5" fill="#FFFFFF" opacity="0.85" />
          {/* amount line */}
          <text x="160" y="158" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="700" fill="#FFFFFF" opacity="0.78">
            Amount
          </text>
          <text x="160" y="186" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="22" fontWeight="900" fill="#FFFFFF">
            $1,245.00
          </text>

          {/* slim accent bar at bottom of screen */}
          <rect x="48" y="200" width="224" height="4" rx="2" fill="#FFFFFF" opacity="0.55" />

          {/* contactless tap zone above keypad */}
          <g transform="translate(160 232)">
            <circle r="14" fill="none" stroke="#0072FF" strokeWidth="2" strokeOpacity="0.45">
              <animate attributeName="r" values="14;24;14" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle r="11" fill="#0072FF" />
            <g stroke="#FFFFFF" strokeWidth="2" fill="none" strokeLinecap="round">
              <path d="M-5 -1 Q-2 -5 1 -1 Q4 -5 7 -1" />
              <path d="M-3 4 Q0 0 3 4" opacity="0.65" />
            </g>
          </g>
          <text x="160" y="270" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#FFFFFF" opacity="0.55">
            TAP TO PAY
          </text>

          {/* KEYPAD */}
          <g transform="translate(54 282)">
            {[0,1,2,3,4,5,6,7,8,9,10,11].map((i) => {
              const col = i % 3;
              const row = Math.floor(i / 3);
              const labels = ["1","2","3","4","5","6","7","8","9","*","0","#"];
              return (
                <g key={i} transform={`translate(${col * 72} ${row * 20})`}>
                  <rect width="60" height="14" rx="4" fill="url(#pt-key)" />
                  <text x="30" y="10" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#FFFFFF" opacity="0.78">
                    {labels[i]}
                  </text>
                </g>
              );
            })}
          </g>

          {/* brand chip on bottom bezel */}
          <text x="160" y="358" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="800" fill="#FFFFFF" opacity="0.32" letterSpacing="1.8">
            SWIPETRONICS
          </text>
        </g>

        {/* FLOATING — small mini transaction card (top-right) */}
        <g transform="translate(400 88)" filter="url(#pt-card-shadow)">
          <rect width="170" height="86" rx="16" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.06" />
          <rect width="170" height="4" rx="2" fill="#0072FF" />
          <text x="14" y="24" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#061A3A" opacity="0.55" letterSpacing="0.6">
            LATEST TRANSACTION
          </text>
          <text x="14" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="20" fontWeight="900" fill="#061A3A">
            +$48.20
          </text>
          {/* mini spark */}
          <g transform="translate(14 60)">
            <polyline points="0,8 12,4 24,6 36,2 48,5 60,1 72,3" fill="none" stroke="#0072FF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <text x="156" y="72" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="800" fill="#0072FF">
            +18.2%
          </text>
        </g>

        {/* FLOATING — secure check badge (bottom-right) */}
        <g transform="translate(430 430)" filter="url(#pt-card-shadow)">
          <rect width="150" height="62" rx="31" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.06" />
          <g transform="translate(12 11)">
            <circle cx="20" cy="20" r="20" fill="#0072FF" />
            {/* shield */}
            <path d="M20 8 L30 12 L30 22 C30 28 26 32 20 34 C14 32 10 28 10 22 L10 12 Z" fill="#FFFFFF" />
            <path d="M14 22 L18 26 L26 18" stroke="#0072FF" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <text x="64" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="700" fill="#061A3A" opacity="0.55">
            End-to-end
          </text>
          <text x="64" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="12.5" fontWeight="800" fill="#061A3A">
            Secure &amp; PCI
          </text>
        </g>

        {/* FLOATING — tiny coins/dot accents */}
        <circle cx="78" cy="118" r="4" fill="#0072FF" opacity="0.85">
          <animate attributeName="cy" values="118;112;118" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="540" cy="290" r="4" fill="#0072FF" opacity="0.8">
          <animate attributeName="cy" values="290;284;290" dur="3.8s" begin="0.6s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   REPORTING DASHBOARD ILLUSTRATION — "Simple reporting and
   business insights" section.
   ──────────────────────────────────────────────────────────── */
function ReportingDashboardIllustration() {
  return (
    <div className="relative w-full" data-testid="reporting-dashboard-illustration">
      <div
        aria-hidden="true"
        className="absolute -inset-3 rounded-[34px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 60% at 60% 50%, rgba(0,114,255,0.20) 0%, transparent 70%)" }}
      />
      <svg viewBox="0 0 600 380" className="relative w-full h-auto" aria-hidden="true">
        <defs>
          <linearGradient id="rd-accent" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0072FF" />
            <stop offset="100%" stopColor="#3A8EFF" />
          </linearGradient>
          <linearGradient id="rd-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#EAF3FF" />
          </linearGradient>
          <filter id="rd-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="20" stdDeviation="20" floodColor="#061A3A" floodOpacity="0.18" />
          </filter>
          <pattern id="rd-dots" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#0072FF" fillOpacity="0.16" />
          </pattern>
        </defs>

        {/* dotted backdrop */}
        <rect x="20" y="40" width="560" height="300" rx="32" fill="url(#rd-dots)" opacity="0.55" />

        {/* Browser/dashboard window */}
        <g transform="translate(60 40)" filter="url(#rd-shadow)">
          <rect width="480" height="300" rx="20" fill="url(#rd-bg)" stroke="#061A3A" strokeOpacity="0.08" />
          {/* browser bar */}
          <rect width="480" height="36" rx="20" fill="#061A3A" />
          <rect y="18" width="480" height="18" fill="#061A3A" />
          <circle cx="20" cy="18" r="4.5" fill="#0072FF" />
          <circle cx="36" cy="18" r="4.5" fill="#3A8EFF" />
          <circle cx="52" cy="18" r="4.5" fill="#FFFFFF" fillOpacity="0.6" />
          <g transform="translate(120 8)">
            <rect width="180" height="20" rx="10" fill="#FFFFFF" fillOpacity="0.12" />
            <text x="14" y="14" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.85">
              dashboard.swipetronics.com
            </text>
          </g>

          {/* sidebar */}
          <rect x="0" y="36" width="68" height="264" fill="#F4F8FF" />
          <g transform="translate(14 56)">
            <rect width="40" height="28" rx="8" fill="url(#rd-accent)" />
            <rect x="0" y="38" width="40" height="20" rx="6" fill="#EAF3FF" />
            <rect x="0" y="66" width="40" height="20" rx="6" fill="#EAF3FF" />
            <rect x="0" y="94" width="40" height="20" rx="6" fill="#EAF3FF" />
            <rect x="0" y="122" width="40" height="20" rx="6" fill="#EAF3FF" />
          </g>

          {/* KPI cards row */}
          <g transform="translate(86 56)">
            {[
              { label: "Volume", value: "$48,210", primary: true },
              { label: "Avg Ticket", value: "$84.20" },
              { label: "Success", value: "99.4%" },
            ].map((k, i) => (
              <g key={k.label} transform={`translate(${i * 130} 0)`}>
                <rect width="124" height="64" rx="12" fill={k.primary ? "url(#rd-accent)" : "#FFFFFF"} stroke="#061A3A" strokeOpacity={k.primary ? 0 : 0.08} />
                <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill={k.primary ? "#FFFFFF" : "#061A3A"} fillOpacity={k.primary ? 0.85 : 0.55} letterSpacing="0.5">
                  {k.label.toUpperCase()}
                </text>
                <text x="14" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="17" fontWeight="900" fill={k.primary ? "#FFFFFF" : "#061A3A"}>
                  {k.value}
                </text>
              </g>
            ))}
          </g>

          {/* main chart card */}
          <g transform="translate(86 138)">
            <rect width="254" height="146" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
            <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="800" fill="#061A3A" fillOpacity="0.55" letterSpacing="0.5">
              SALES TREND
            </text>
            <text x="14" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="11" fontWeight="800" fill="#0072FF">
              +18.2% week over week
            </text>
            {/* grid lines */}
            {[60, 80, 100, 120].map((y) => (
              <line key={y} x1="14" y1={y} x2="240" y2={y} stroke="#061A3A" strokeOpacity="0.06" />
            ))}
            {/* area fill */}
            <path
              d="M14,116 L42,98 L70,104 L98,82 L126,86 L154,68 L182,72 L210,52 L240,58 L240,130 L14,130 Z"
              fill="url(#rd-accent)"
              opacity="0.22"
            />
            {/* line */}
            <polyline
              points="14,116 42,98 70,104 98,82 126,86 154,68 182,72 210,52 240,58"
              fill="none"
              stroke="url(#rd-accent)"
              strokeWidth="2.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* end dot */}
            <circle cx="240" cy="58" r="4.5" fill="#FFFFFF" stroke="#0072FF" strokeWidth="2.5">
              <animate attributeName="r" values="4.5;7;4.5" dur="2.2s" repeatCount="indefinite" />
            </circle>
          </g>

          {/* right column — mini list */}
          <g transform="translate(350 138)">
            <rect width="118" height="146" rx="14" fill="#EAF3FF" />
            <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9.5" fontWeight="800" fill="#061A3A" fillOpacity="0.55" letterSpacing="0.5">
              TOP CATEGORIES
            </text>
            {[
              { label: "Card Present", pct: "62%", w: 86 },
              { label: "Online", pct: "24%", w: 40 },
              { label: "Invoice", pct: "8%", w: 18 },
              { label: "Other", pct: "6%", w: 12 },
            ].map((c, i) => (
              <g key={c.label} transform={`translate(14 ${36 + i * 22})`}>
                <text x="0" y="6" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A">
                  {c.label}
                </text>
                <text x="90" y="6" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#0072FF">
                  {c.pct}
                </text>
                <rect x="0" y="10" width="90" height="4" rx="2" fill="#FFFFFF" />
                <rect x="0" y="10" width={c.w} height="4" rx="2" fill="url(#rd-accent)" />
              </g>
            ))}
          </g>
        </g>

        {/* floating coin/accent */}
        <circle cx="60" cy="100" r="5" fill="#0072FF" opacity="0.85">
          <animate attributeName="cy" values="100;94;100" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="552" cy="270" r="4" fill="#0072FF" opacity="0.7">
          <animate attributeName="cy" values="270;264;270" dur="3.6s" begin="0.4s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   MOBILE PAYMENT / TAP ILLUSTRATION — "Payments and reporting
   on the go." section.
   ──────────────────────────────────────────────────────────── */
function MobilePaymentTapIllustration() {
  return (
    <div className="relative w-full" data-testid="mobile-payment-tap-illustration">
      <div
        aria-hidden="true"
        className="absolute -inset-3 rounded-[34px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 60% at 40% 50%, rgba(0,114,255,0.22) 0%, transparent 70%)" }}
      />
      <svg viewBox="0 0 600 420" className="relative w-full h-auto" aria-hidden="true">
        <defs>
          <linearGradient id="mt-terminal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10182A" />
            <stop offset="100%" stopColor="#061A3A" />
          </linearGradient>
          <linearGradient id="mt-screen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0072FF" />
            <stop offset="100%" stopColor="#3A8EFF" />
          </linearGradient>
          <linearGradient id="mt-card" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0A2552" />
            <stop offset="100%" stopColor="#0072FF" />
          </linearGradient>
          <linearGradient id="mt-glare" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.22" />
            <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <filter id="mt-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="22" stdDeviation="22" floodColor="#061A3A" floodOpacity="0.25" />
          </filter>
          <pattern id="mt-dots" width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#0072FF" fillOpacity="0.16" />
          </pattern>
        </defs>

        <rect x="20" y="40" width="560" height="340" rx="32" fill="url(#mt-dots)" opacity="0.55" />

        {/* TERMINAL — center-left, slightly tilted */}
        <g transform="translate(170 90) rotate(-6 100 130)" filter="url(#mt-shadow)">
          {/* body */}
          <rect width="200" height="280" rx="22" fill="url(#mt-terminal)" />
          {/* inner bezel */}
          <rect x="12" y="12" width="176" height="256" rx="16" fill="#06080F" />
          {/* glare highlight */}
          <rect width="200" height="280" rx="22" fill="url(#mt-glare)" opacity="0.4" />
          {/* screen */}
          <rect x="22" y="22" width="156" height="124" rx="10" fill="url(#mt-screen)" />
          <ellipse cx="100" cy="50" rx="80" ry="30" fill="#FFFFFF" opacity="0.16" />
          {/* amount */}
          <text x="100" y="78" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="800" fill="#FFFFFF" opacity="0.82" letterSpacing="1.5">
            TAP TO PAY
          </text>
          <text x="100" y="118" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="24" fontWeight="900" fill="#FFFFFF">
            $84.20
          </text>

          {/* contactless target */}
          <g transform="translate(100 178)">
            <circle r="14" fill="none" stroke="#0072FF" strokeWidth="2" strokeOpacity="0.4">
              <animate attributeName="r" values="14;24;14" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle r="11" fill="#0072FF" />
            <g stroke="#FFFFFF" strokeWidth="2.2" fill="none" strokeLinecap="round">
              <path d="M-5 -1 Q-2 -5 1 -1 Q4 -5 7 -1" />
              <path d="M-3 4 Q0 0 3 4" opacity="0.65" />
            </g>
          </g>

          {/* small keypad */}
          <g transform="translate(34 218)">
            {[0,1,2,3,4,5].map((i) => {
              const col = i % 3;
              const row = Math.floor(i / 3);
              return (
                <rect
                  key={i}
                  x={col * 46}
                  y={row * 16}
                  width="38"
                  height="10"
                  rx="3"
                  fill="#1F2A44"
                />
              );
            })}
          </g>
        </g>

        {/* FLOATING CARD being tapped (front-right) */}
        <g transform="translate(370 140) rotate(12)" filter="url(#mt-shadow)">
          <rect width="200" height="124" rx="14" fill="url(#mt-card)" />
          <rect width="200" height="124" rx="14" fill="url(#mt-glare)" />
          {/* chip */}
          <rect x="18" y="36" width="32" height="26" rx="4" fill="#C5A878" />
          <rect x="22" y="40" width="24" height="18" rx="3" fill="#D8B888" />
          <rect x="24" y="43" width="20" height="1.4" rx="0.7" fill="#9C7E5A" />
          <rect x="24" y="47" width="20" height="1.4" rx="0.7" fill="#9C7E5A" />
          <rect x="24" y="51" width="20" height="1.4" rx="0.7" fill="#9C7E5A" />
          {/* contactless icon */}
          <g transform="translate(60 40)" stroke="#FFFFFF" strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.92">
            <path d="M0 8 Q4 0 8 8" />
            <path d="M-3 14 Q4 -1 11 14" opacity="0.7" />
          </g>
          {/* number */}
          <text x="18" y="86" fontFamily="ui-monospace, Menlo, monospace" fontSize="11" fontWeight="700" fill="#FFFFFF" opacity="0.92" letterSpacing="1.2">
            ●●●●  ●●●●  ●●●●  1245
          </text>
          <text x="18" y="104" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#FFFFFF" opacity="0.6">
            VALID 12/29
          </text>
          {/* brand */}
          <g transform="translate(146 96)">
            <circle r="10" fill="#EB001B" />
            <circle cx="14" r="10" fill="#F79E1B" />
          </g>
        </g>

        {/* ripple emanating from terminal screen */}
        <g transform="translate(280 220)" opacity="0.5">
          <circle r="38" fill="none" stroke="#0072FF" strokeWidth="2">
            <animate attributeName="r" values="20;60;20" dur="2.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.55;0;0.55" dur="2.6s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* floating mini approval chip */}
        <g transform="translate(70 310)" filter="url(#mt-shadow)">
          <rect width="160" height="56" rx="28" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
          <g transform="translate(10 8)">
            <circle cx="20" cy="20" r="20" fill="url(#mt-screen)" />
            <path d="M11 20 L18 27 L29 16" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <text x="60" y="28" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" opacity="0.55">
            Approved
          </text>
          <text x="60" y="44" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#061A3A">
            $84.20 · 9:41 AM
          </text>
        </g>

        {/* accents */}
        <circle cx="84" cy="92" r="4" fill="#0072FF" opacity="0.85">
          <animate attributeName="cy" values="92;86;92" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="520" cy="330" r="4" fill="#0072FF" opacity="0.75">
          <animate attributeName="cy" values="330;324;330" dur="3.8s" begin="0.4s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo {...getSeoForPath("/")} />
      {/* 1. HERO — bright light-blue overlay */}
      <section className="relative bg-[var(--swipe-accent)] text-white" style={{ overflow: 'visible', minHeight: '640px', paddingBottom: '50px' }}>
        {/* Background image layer — full width */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMG}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center' }}
          />
        </div>
        {/* Bright blue overlay — softer so the photo shows through */}
        <div className="absolute inset-0" style={{
          background:
            'linear-gradient(180deg, rgba(0,114,255,0.50) 0%, rgba(58,142,255,0.42) 50%, rgba(125,182,255,0.36) 100%)'
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 35%, rgba(255,255,255,0.18) 0%, transparent 65%)'
        }} />

        {/* Spacer to clear the absolutely-positioned transparent header (~150px on desktop) */}
        <div className="relative h-[150px] md:h-[170px]" />

        <div className="container-swipe relative pt-4 md:pt-8 pb-10 md:pb-14 text-center fade-in-up">
          <h1 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] xl:text-[3.875rem] leading-[1.1] font-extrabold text-white tracking-[-0.025em] max-w-5xl mx-auto drop-shadow-[0_3px_18px_rgba(6,26,58,0.45)]">
            Easy, reliable payment processing for modern businesses.
          </h1>
          <p className="mt-6 text-[17px] md:text-[18px] text-white/95 leading-[1.6] max-w-xl mx-auto drop-shadow-[0_2px_10px_rgba(6,26,58,0.35)]">
            Swipetronics is a merchant services company helping businesses lower
            processing costs, accept payments faster, upgrade POS systems, and get
            dependable support.
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/get-started"
              className="inline-flex items-center gap-2 bg-[var(--swipe-accent)] text-white uppercase tracking-[0.18em] font-extrabold text-[13.5px] px-9 py-4 rounded-md shadow-[0_18px_40px_-10px_rgba(0,114,255,0.65)] hover:bg-[#005FE0] hover:-translate-y-0.5 hover:shadow-[0_28px_56px_-10px_rgba(0,114,255,0.75)] transition-all duration-300"
              data-testid="hero-cta-get-started"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 2. TRUST CARDS — placed between hero and the next section */}
      <section className="bg-white border-b border-line relative">
        <div className="container-swipe relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-8 relative z-20 pb-12 md:pb-14" style={{ marginTop: '-120px' }}>
            {[
              {
                icon: ReceiptText,
                title: "Transparent Pricing",
                desc: "Clear, line-item statements with no surprises — see exactly where every dollar goes.",
                to: "/services#traditional-processing",
              },
              {
                icon: Lock,
                title: "Fully Encrypted",
                desc: "End-to-end EMV encryption and tokenization on every transaction.",
                to: "/services#security",
              },
              {
                icon: ShieldCheck,
                title: "Safe & Secure",
                desc: "PCI-compliant infrastructure with built-in chargeback support.",
                to: "/services#security",
              },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <Link
                  key={t.title}
                  to={t.to}
                  data-testid={`trust-card-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative block bg-gradient-to-br from-[var(--swipe-accent-soft)] via-white to-white border border-[var(--swipe-accent)]/15 rounded-2xl p-10 md:p-12 min-h-[320px] flex flex-col items-center text-center shadow-[0_24px_60px_-22px_rgba(0,114,255,0.25)] transition-all duration-300 hover:-translate-y-2 hover:border-[var(--swipe-accent)]/55 hover:shadow-[0_38px_82px_-22px_rgba(0,114,255,0.45)] cursor-pointer overflow-hidden"
                >
                  {/* top accent line */}
                  <span aria-hidden="true" className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[var(--swipe-accent)] to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* subtle corner glow */}
                  <span aria-hidden="true" className="absolute -top-12 -right-12 w-52 h-52 rounded-full bg-[var(--swipe-accent)] opacity-[0.06] group-hover:opacity-[0.12] blur-3xl transition-opacity duration-500" />
                  <div className="relative flex-1 flex flex-col items-center">
                    <div className="w-[84px] h-[84px] rounded-2xl bg-white border border-[var(--swipe-accent)]/25 text-[var(--swipe-accent)] flex items-center justify-center shadow-[0_16px_32px_-10px_rgba(0,114,255,0.35)] transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110 group-hover:-rotate-[6deg] group-hover:border-[var(--swipe-accent)] group-hover:shadow-[0_18px_36px_-8px_rgba(0,114,255,0.55)]">
                      <Icon className="w-10 h-10" strokeWidth={2} />
                    </div>
                    <h3 className="mt-7 font-display text-[24px] font-bold text-[var(--swipe-navy)] leading-tight tracking-[-0.01em] transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                      {t.title}
                    </h3>
                    <p className="mt-4 text-[15.5px] text-slate-600 leading-[1.7] max-w-[300px]">{t.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. WHY CHOOSE */}
      <section className="section pt-2 md:pt-4">
        <div className="container-swipe grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 lg:order-1">
            <div className="overline mb-4">Why Swipetronics</div>
            <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3.25rem] font-bold text-[var(--swipe-navy)] leading-[1.1]">
              Payment solutions built around your business.
            </h2>
            <p className="mt-6 text-[16.5px] text-slate-600 leading-relaxed">
              Swipetronics gives business owners clear payment options, reliable equipment, and
              support through every step of the switch. Whether you need lower traditional rates,
              a cash discount program, a new POS system, or mobile payment options — we help you
              choose the setup that fits.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {[
                "Payment processing & management",
                "POS systems and terminals",
                "Cash discount & dual pricing",
                "Transparent line-item pricing",
                "Secure payment technology",
                "Dedicated support and setup help",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[15px] text-slate-700">
                  <Check className="w-4 h-4 text-[var(--swipe-accent)] mt-1 shrink-0" strokeWidth={3} />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/services" className="btn-secondary-swipe" data-testid="why-cta-services">
                Explore Services <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/about" className="btn-ghost-swipe">About Swipetronics <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <PaymentTerminalIllustration />
          </div>
        </div>
      </section>

      {/* 4. BENEFITS — illustration left, smaller benefit cards right */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1fr,1.05fr] gap-10 lg:gap-16 items-center">
            {/* LEFT: custom flat-fintech illustration (growth / tailored solutions) */}
            <div className="relative">
              <div className="sticky top-32">
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-4 rounded-[40px] pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse 70% 70% at 30% 30%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                    }}
                  />
                  <ParticleField
                    density={26}
                    connectDistance={110}
                    className="absolute inset-0 w-full h-full rounded-[40px]"
                  />
                  <GrowthIllustration className="relative w-full h-auto max-w-[560px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
                </div>
                <div className="mt-6 flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-white border border-line px-4 py-2.5 rounded-full text-xs font-bold text-[var(--swipe-navy)] shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                    Built for modern businesses
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: cards */}
            <div>
              <div className="overline mb-4">Benefits</div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.1]">
                Tailored payment solutions to help your business grow.
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-relaxed">
                Everything Swipetronics brings to the table is designed to do one thing: help you run
                payments more profitably, with less friction, on equipment that actually works.
              </p>

              <div className="mt-9 grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Wallet, t: "More payment options", d: "Tap, dip, swipe, mobile wallets, online checkout, and ACH.", to: "/services" },
                  { icon: Zap, t: "Faster checkout", d: "Quicker terminal response and fewer steps at the counter.", to: "/pos-systems" },
                  { icon: PiggyBank, t: "Reduced processing costs", d: "Cash discount, dual pricing, and tighter traditional rates.", to: "/cash-discount" },
                  { icon: Lock, t: "Secure transactions", d: "EMV, tokenization, encryption, and PCI compliance support.", to: "/services#security" },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <Link
                      key={b.t}
                      to={b.to}
                      data-testid={`benefit-card-${b.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group relative block bg-white border border-line rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:bg-gradient-to-br hover:from-white hover:to-[var(--swipe-accent-soft)] hover:shadow-[0_18px_36px_-18px_rgba(0,114,255,0.25)] cursor-pointer overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-[var(--swipe-accent)] opacity-0 group-hover:opacity-[0.07] blur-2xl transition-opacity duration-500" />
                      <div className="relative">
                        <div className="w-11 h-11 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110 group-hover:rotate-[-4deg]">
                          <Icon className="w-5 h-5" strokeWidth={2.25} />
                        </div>
                        <h3 className="mt-4 text-[15px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                          {b.t}
                        </h3>
                        <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{b.d}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PRODUCTS & TECH — cards left, image right */}
      <section className="section bg-[var(--swipe-muted)] border-y border-line">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1.1fr,1fr] gap-10 lg:gap-16 items-start">
            {/* LEFT: heading + cards */}
            <div>
              <div className="overline mb-4">Products & Technology</div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[2.75rem] font-bold text-[var(--swipe-navy)] leading-[1.1]">
                Accept payments anywhere your customers are.
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-relaxed max-w-xl">
                From in-store hardware to mobile wallets, online checkout, and ACH — Swipetronics
                supports every way your customers want to pay.
              </p>

              <div className="mt-9 grid sm:grid-cols-2 gap-3.5">
                {[
                  { icon: Monitor, t: "POS Systems", d: "Touchscreen systems for restaurants, retail, and salons.", to: "/pos-systems" },
                  { icon: Terminal, t: "Countertop Terminals", d: "Compact, reliable in-store hardware.", to: "/products#countertop" },
                  { icon: Smartphone, t: "Mobile Payments", d: "Wireless handhelds and phone readers.", to: "/services#mobile-payments" },
                  { icon: Wallet, t: "Digital Wallets", d: "Apple Pay, Google Pay, Samsung Pay.", to: "/services#credit-card-processing" },
                  { icon: ScanLine, t: "Tap / Dip / Swipe", d: "Every acceptance method at the counter.", to: "/services#credit-card-processing" },
                  { icon: Globe, t: "E-Commerce Payments", d: "Plug-and-play online checkout.", to: "/services#ecommerce" },
                  { icon: Cpu, t: "Virtual Terminal", d: "Card-not-present from any browser.", to: "/services#virtual-terminal" },
                  { icon: Repeat, t: "ACH Payments", d: "Lower-cost bank transfers.", to: "/services" },
                ].map((p) => {
                  const Icon = p.icon;
                  return (
                    <Link
                      key={p.t}
                      to={p.to}
                      data-testid={`product-card-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group relative block bg-white border border-line rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:bg-gradient-to-br hover:from-white hover:to-[var(--swipe-accent-soft)] hover:shadow-[0_18px_36px_-18px_rgba(0,114,255,0.25)] cursor-pointer overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-[var(--swipe-accent)] opacity-0 group-hover:opacity-[0.07] blur-2xl transition-opacity duration-500" />
                      <div className="relative">
                        <div className="w-10 h-10 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110 group-hover:rotate-[-4deg]">
                          <Icon className="w-5 h-5" strokeWidth={2.25} />
                        </div>
                        <h3 className="mt-4 text-[14.5px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                          {p.t}
                        </h3>
                        <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{p.d}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="mt-8">
                <Link to="/products" className="btn-secondary-swipe">
                  View all products <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* RIGHT: custom flat-fintech illustration (anywhere / mobile payments) */}
            <div className="relative">
              <div className="sticky top-32">
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-4 rounded-[40px] pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse 70% 70% at 70% 30%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                    }}
                  />
                  <ParticleField
                    density={26}
                    connectDistance={110}
                    className="absolute inset-0 w-full h-full rounded-[40px]"
                  />
                  <AnywhereIllustration className="relative w-full h-auto max-w-[560px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
                </div>
                <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                  <div className="inline-flex items-center gap-2 bg-white border border-line px-4 py-2.5 rounded-full text-xs font-bold text-[var(--swipe-navy)] shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                    Tap · Dip · Swipe · Online
                  </div>
                  <div className="inline-flex items-center gap-2 bg-[var(--swipe-navy)] px-4 py-2.5 rounded-full text-xs font-bold text-white shadow-sm">
                    <ShieldCheck className="w-3.5 h-3.5 text-[var(--swipe-accent)]" />
                    EMV &amp; PCI
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. COMPARISON */}
      <section className="relative bg-gradient-to-b from-white via-[var(--swipe-accent-soft)] to-white overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[760px] h-[760px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,114,255,0.08) 0%, transparent 65%)" }} />
        <div className="container-swipe-wide section relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline mb-4" style={{ color: "var(--swipe-accent)" }}>Comparison</div>
            <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold leading-[1.12] text-[var(--swipe-navy)]">
              Compare Swipetronics with other providers.
            </h2>
            <p className="mt-5 text-slate-600 text-[16px] max-w-xl mx-auto">Same processing categories. Very different experience.</p>
          </div>

          <div className="mt-14 relative max-w-5xl mx-auto">
            {/* outer soft glow */}
            <div aria-hidden="true" className="absolute -inset-6 rounded-[36px] pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,114,255,0.15) 0%, transparent 70%)" }} />

            {/* Recommended badge */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-5 z-20">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--swipe-accent)] text-white text-[11px] uppercase tracking-[0.20em] font-extrabold shadow-[0_14px_30px_-10px_rgba(0,114,255,0.65)]">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={3} />
                Recommended Choice
              </span>
            </div>

            {/* Unified comparison card */}
            <div className="relative rounded-3xl border border-[var(--swipe-accent)]/15 bg-white overflow-hidden shadow-[0_36px_70px_-26px_rgba(0,114,255,0.30)]">
              {/* highlight band behind Swipetronics column */}
              <div
                aria-hidden="true"
                className="hidden md:block absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: "calc(40% + 0px)",
                  right: "27%",
                  background:
                    "linear-gradient(180deg, rgba(0,114,255,0.10) 0%, rgba(0,114,255,0.04) 100%)",
                  borderLeft: "1px solid rgba(0,114,255,0.30)",
                  borderRight: "1px solid rgba(0,114,255,0.30)",
                }}
              />

              {/* HEADER ROW */}
              <div className="relative grid grid-cols-[1.6fr,1fr,1fr] md:grid-cols-[1.4fr,1fr,1fr] border-b border-line">
                <div className="px-6 md:px-8 py-5 text-[10.5px] uppercase tracking-[0.22em] font-extrabold text-slate-500">
                  Feature
                </div>
                <div className="px-3 md:px-5 py-5 text-center">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--swipe-accent)]/15 border border-[var(--swipe-accent)]/30 text-[9.5px] uppercase tracking-[0.22em] font-extrabold text-[var(--swipe-accent)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                    <span className="hidden sm:inline">Built for Business</span>
                    <span className="sm:hidden">Built for You</span>
                  </div>
                  <h3 className="mt-2.5 font-display-tight text-base sm:text-lg md:text-xl font-extrabold tracking-[-0.01em] text-[var(--swipe-navy)]">
                    Swipetronics
                  </h3>
                </div>
                <div className="px-3 md:px-5 py-5 text-center">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-line text-[9.5px] uppercase tracking-[0.22em] font-extrabold text-slate-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    Typical
                  </div>
                  <h3 className="mt-2.5 font-display-tight text-base sm:text-lg md:text-xl font-extrabold tracking-[-0.01em] text-slate-500">
                    Other providers
                  </h3>
                </div>
              </div>

              {/* ROWS */}
              {[
                { label: "Transparent pricing",      other: "x" },
                { label: "Dedicated support",        other: "partial" },
                { label: "POS system options",       other: "x" },
                { label: "Cash discount program",    other: "partial" },
                { label: "Free POS eligibility",     other: "x" },
                { label: "Fast setup",               other: "partial" },
                { label: "Secure processing",        other: "x" },
                { label: "Chargeback support",       other: "partial" },
                { label: "Easy-to-understand options", other: "x" },
              ].map((row, i) => (
                <div
                  key={row.label}
                  className={`relative grid grid-cols-[1.6fr,1fr,1fr] md:grid-cols-[1.4fr,1fr,1fr] items-center ${i !== 0 ? "border-t border-line" : ""}`}
                >
                  <div className="px-6 md:px-8 py-4 text-[13.5px] sm:text-[14.5px] font-semibold text-[var(--swipe-navy)]">
                    {row.label}
                  </div>
                  <div className="px-3 md:px-5 py-4 flex justify-center">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--swipe-accent)] shadow-[0_10px_20px_-8px_rgba(0,114,255,0.55)]">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </span>
                  </div>
                  <div className="px-3 md:px-5 py-4 flex justify-center">
                    <span
                      className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${
                        row.other === "partial"
                          ? "bg-slate-100 border border-line"
                          : "bg-white border border-line"
                      }`}
                    >
                      {row.other === "partial" ? (
                        <Check className="w-[18px] h-[18px] text-slate-400" strokeWidth={2.6} />
                      ) : (
                        <X className="w-[18px] h-[18px] text-slate-400" strokeWidth={2.6} />
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/savings-analysis" className="btn-primary-swipe" data-testid="comparison-cta">
              Compare your current statement <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="btn-outline-light">See all services</Link>
          </div>
        </div>
      </section>

      {/* 7. FEATURES */}
      <section className="section">
        <div className="container-swipe">
          {/* 2-column header with ecosystem illustration on the left */}
          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-10 lg:gap-16 items-center mb-14">
            {/* LEFT: ecosystem illustration */}
            <div className="relative order-2 lg:order-1">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 rounded-[40px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 70% at 30% 50%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                  }}
                />
                <ParticleField
                  density={24}
                  connectDistance={110}
                  className="absolute inset-0 w-full h-full rounded-[40px]"
                />
                <EcosystemIllustration className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
              </div>
            </div>

            {/* RIGHT: heading + intro */}
            <div className="order-1 lg:order-2">
              <div className="overline mb-4">Our Features</div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3.25rem] font-bold text-[var(--swipe-navy)] leading-[1.1]">
                Everything you need to run payments smoothly.
              </h2>
              <p className="mt-5 text-[17px] text-slate-600 leading-relaxed max-w-xl">
                From in-store terminals to online checkout and recurring billing — Swipetronics
                covers the full payment stack with the security tools modern businesses expect.
              </p>
            </div>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: CreditCard, t: "Payment Processing", d: "Accept all major card brands across in-store, mobile, and online — with next-day funding options.", to: "/services#credit-card-processing" },
              { icon: Server, t: "Payment Gateway Integration", d: "Connect your shopping cart, custom website, or software with a secure, tokenized gateway.", to: "/products#gateway" },
              { icon: ShieldCheck, t: "Security & Compliance", d: "EMV, tokenization, and PCI assistance baked into every transaction — and chargeback support too.", to: "/services#security" },
              { icon: Repeat, t: "Recurring Billing & Invoicing", d: "Subscriptions, scheduled payments, and email invoices — collect on autopilot.", to: "/services#virtual-terminal" },
              { icon: Settings, t: "Custom Payment Solutions", d: "Industry-fit setups built around how your business actually operates, not the other way around.", to: "/services" },
              { icon: Wallet, t: "Cash Discount & Dual Pricing", d: "Lower your effective rate with clear, compliant pricing your customers understand.", to: "/cash-discount" },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <Link
                  key={f.t}
                  to={f.to}
                  data-testid={`feature-card-${f.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative block bg-white border border-line rounded-xl p-8 lg:p-9 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] hover:bg-gradient-to-br hover:from-white hover:to-[var(--swipe-accent-soft)] hover:shadow-[0_24px_50px_-22px_rgba(0,114,255,0.28)] cursor-pointer overflow-hidden min-h-[260px] flex flex-col"
                >
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[var(--swipe-accent)] opacity-0 group-hover:opacity-[0.07] blur-3xl transition-opacity duration-500" />
                  <div className="relative flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div className="w-16 h-16 rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110 group-hover:rotate-[-6deg] group-hover:shadow-[0_10px_24px_-8px_rgba(0,114,255,0.55)]">
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <div className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-slate-400 transition-all duration-300 group-hover:border-[var(--swipe-accent)] group-hover:bg-[var(--swipe-accent)] group-hover:text-white">
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </div>
                    </div>
                    <h3 className="mt-7 font-display text-[22px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                      {f.t}
                    </h3>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{f.d}</p>
                    <div className="mt-auto pt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--swipe-navy)] opacity-60 group-hover:opacity-100 group-hover:text-[var(--swipe-accent)] transition-all">
                      Learn more
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8. DASHBOARD */}
      <section className="bg-[var(--swipe-muted)] section border-y border-line">
        <div className="container-swipe grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-4">Merchant Dashboard</div>
            <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
              Simple reporting and business insights.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Track sales, review transactions, monitor deposits, and understand your business
              performance with clear payment reporting tools.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {[
                "Real-time transactions",
                "Deposit & batch reporting",
                "Refunds & chargebacks",
                "Tips, taxes, & item breakdowns",
              ].map((i) => (
                <li key={i} className="flex items-center gap-2.5 text-[15px] text-slate-700">
                  <Check className="w-4 h-4 text-[var(--swipe-accent)]" strokeWidth={3} />
                  {i}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/contact" className="btn-primary-swipe" data-testid="dashboard-demo">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div>
            <ReportingDashboardIllustration />
          </div>
        </div>
      </section>

      {/* 9. STATS */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute -top-20 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,114,255,0.10) 0%, transparent 65%)" }} />
        <div className="container-swipe relative py-10 lg:py-12">
          <div className="text-center mb-7">
            <div className="text-[10.5px] uppercase tracking-[0.24em] font-extrabold mb-2" style={{ color: "#7DB6FF" }}>
              Trusted Performance
            </div>
            <h2 className="font-display-tight text-[1.6rem] sm:text-[1.85rem] font-extrabold tracking-[-0.015em]" style={{ color: "#FFFFFF" }}>
              Experience you can count on.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4 max-w-4xl mx-auto">
            {[
              { n: 250, s: "+", l: "Businesses Served", icon: Building2 },
              { n: 400, s: "+", l: "Payment Solutions Installed", icon: CreditCard },
              { n: 7, s: "+", l: "Years of Industry Experience", icon: Sparkles },
            ].map((st) => {
              const Icon = st.icon;
              return (
                <div
                  key={st.l}
                  data-testid={`stat-${st.l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] via-white/[0.04] to-white/[0.02] p-5 lg:p-6 text-center transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--swipe-accent)]/50 hover:shadow-[0_20px_40px_-18px_rgba(0,114,255,0.45)] overflow-hidden"
                >
                  <span aria-hidden="true" className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[var(--swipe-accent)] opacity-0 group-hover:opacity-[0.18] blur-3xl transition-opacity duration-500" />
                  <div className="relative flex items-center justify-center gap-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--swipe-accent)]/18 border border-[var(--swipe-accent)]/30 text-[var(--swipe-accent)] transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white shrink-0">
                      <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" strokeWidth={2.4} />
                    </span>
                    <span className="text-3xl lg:text-[2.5rem] font-extrabold tracking-tight leading-none" style={{ color: "#FFFFFF" }}>
                      <Counter end={st.n} suffix={st.s} />
                    </span>
                  </div>
                  <div className="relative mt-3 text-[11.5px] font-bold uppercase tracking-[0.16em]" style={{ color: "rgba(255,255,255,0.65)" }}>
                    {st.l}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10. INDUSTRY TRUST CARDS */}
      <section className="section">
        <div className="container-swipe">
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <div className="overline mb-4">Industries we serve</div>
            <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
              Built for the businesses we serve every day.
            </h2>
            <p className="mt-5 text-slate-600 text-[16px] leading-[1.7] max-w-2xl mx-auto">
              From quick-service counters to specialty retail and service-based shops, Swipetronics tailors processing, POS, and support to how your business actually runs.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {[
              {
                icon: Utensils,
                title: "Restaurants & Delis",
                bullets: [
                  "Quick checkout for high-volume counters",
                  "Cash discount options for tighter margins",
                  "POS systems built for tickets, tabs & tableside",
                ],
              },
              {
                icon: ShoppingBag,
                title: "Convenience Stores",
                bullets: [
                  "Fast tap, dip, and contactless acceptance",
                  "Lower fees for high transaction counts",
                  "Inventory-aware POS options available",
                ],
              },
              {
                icon: Store,
                title: "Retail Shops",
                bullets: [
                  "Clean, transparent statements every month",
                  "Modern terminals and POS hardware options",
                  "Real human support after setup, not just before",
                ],
              },
              {
                icon: Briefcase,
                title: "Service Businesses",
                bullets: [
                  "Invoicing and Pay-Now link options",
                  "Mobile payments for on-site and in-the-field work",
                  "Recurring billing for ongoing customers",
                ],
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  data-testid={`industry-card-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative bg-white border border-line rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)]/40 hover:shadow-[0_24px_50px_-22px_rgba(0,114,255,0.32)] shadow-[0_14px_36px_-30px_rgba(6,26,58,0.30)] overflow-hidden flex flex-col"
                >
                  <span aria-hidden="true" className="absolute top-0 left-6 right-6 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />
                  <div className="w-12 h-12 rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110 group-hover:-rotate-3 shadow-[0_10px_22px_-12px_rgba(0,114,255,0.35)]">
                    <Icon className="w-6 h-6" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-5 font-display-tight text-[18px] font-extrabold text-[var(--swipe-navy)] leading-tight tracking-[-0.01em]">
                    {card.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5 flex-1">
                    {card.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[13.5px] text-slate-600 leading-[1.6]">
                        <span className="mt-1 w-4 h-4 rounded-full bg-[var(--swipe-accent)]/12 text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3" strokeWidth={3.2} />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 11. PARTNERS */}
      <section className="bg-[var(--swipe-muted)] border-y border-line py-14">
        <div className="container-swipe text-center max-w-2xl mx-auto mb-10">
          <div className="overline mb-3">Technology partners & POS options</div>
          <h2 className="font-display-tight text-2xl sm:text-3xl font-bold text-[var(--swipe-navy)]">
            Backed by industry-leading payment technology.
          </h2>
        </div>
        <ClientOnly>
          <Marquee gradient={false} speed={32} pauseOnHover>
            {["POS Systems", "Payment Gateways", "Card Terminals", "Security Tools", "Reporting Tools", "E-Comm Plugins", "Mobile Hardware", "Compliance"].map((p) => (
              <div key={p} className="mx-3 px-6 py-3 bg-white border border-line rounded-md flex items-center gap-2.5 min-w-[200px]">
                <div className="w-8 h-8 rounded bg-[var(--swipe-accent-soft)] flex items-center justify-center text-[var(--swipe-accent)]">
                  <Building2 className="w-4 h-4" strokeWidth={2.25} />
                </div>
                <div className="font-semibold text-[13px] text-[var(--swipe-navy)]">{p}</div>
              </div>
            ))}
        </Marquee>
        </ClientOnly>
      </section>

      {/* 12. MOBILE */}
      <section className="section">
        <div className="container-swipe grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <MobilePaymentTapIllustration />
          </div>
          <div className="order-1 lg:order-2">
            <div className="overline mb-4">Mobile & Reporting</div>
            <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
              Payments and reporting on the go.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Give your business flexible payment tools, mobile options, and access to important
              payment information when you need it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/services#mobile-payments" className="btn-secondary-swipe" data-testid="mobile-cta-learn">
                Learn About Mobile Payments <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline-swipe" data-testid="mobile-cta-contact">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* 13. RESOURCES */}
      <section className="bg-[var(--swipe-muted)] section border-y border-line">
        <div className="container-swipe">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
            <div className="max-w-xl">
              <div className="overline mb-4">Resources</div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-bold text-[var(--swipe-navy)] leading-[1.12]">
                Resources for business owners.
              </h2>
            </div>
            <Link to="/resources" className="btn-ghost-swipe shrink-0">All articles <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { t: "How to Read a Merchant Statement", c: "Statements", to: "/resources/how-to-read-a-merchant-statement" },
              { t: "Cash Discount vs Traditional: Which Is Better?", c: "Pricing", to: "/resources/cash-discount-vs-traditional" },
              { t: "Questions to Ask Before Choosing a Processor", c: "Selection", to: "/resources/questions-before-choosing-a-processor" },
            ].map((b) => (
              <Link key={b.t} to={b.to} className="bg-white border border-line rounded-md p-6 hover-lift block group">
                <div className="text-[11px] font-semibold text-[var(--swipe-accent)] uppercase tracking-[0.14em]">{b.c}</div>
                <div className="mt-3 text-[15px] font-bold text-[var(--swipe-navy)] leading-snug">{b.t}</div>
                <div className="mt-5 text-[13px] font-semibold text-[var(--swipe-navy)] inline-flex items-center gap-1 group-hover:text-[var(--swipe-accent)]">
                  Read article <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 14. FINAL CTA */}
      <CTASection />
    </div>
  );
}
